import { useEffect, useState } from "react";
import CoreLayout from "../../components/layout/CoreLayout";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import { cardBorderClassName } from "../../lib/cardStyles";

// -----------------------------------------------------------------------------
// Backend API base (MUST be nginx/Flask, not Vercel)
// -----------------------------------------------------------------------------
const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

const PHONE_PROVISIONING_PLACEHOLDER =
  "We're setting up your dedicated phone number — you'll get an email within one business day with your number and simple instructions for forwarding your existing business line to it. Your customers keep calling the number they already know; your AI receptionist answers.";

const PHONE_FORWARDING_HINT =
  "Forward your existing business line to this number to put your receptionist to work. Forwarding instructions were included in your activation email.";

/** Curated ElevenLabs presets (extend as needed). IDs are ElevenLabs `voice_id` values. */
const ELEVENLABS_VOICE_CATALOG: ReadonlyArray<{ id: string; label: string }> = [
  { id: "FGY2WhTYpPnrIDTdsKH5", label: "Lora — warm default" },
  { id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel — natural" },
  { id: "AZnzlk1XvdvUeBnXmlld", label: "Domi — strong" },
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Bella — friendly" },
  { id: "ErXwobaYiN019PkySvjV", label: "Antoni — professional" },
  { id: "MF3mGyEYCl7XYWbV9V6O", label: "Elli — energetic" },
  { id: "TxGEqnHWrfWFTfGW9XjX", label: "Josh — deep" },
  { id: "VR6AewLTigWG4xSOukaG", label: "Arnold — crisp" },
  { id: "pNInz6obpgDQGcFmaJgB", label: "Adam — narrator" },
  { id: "yoZ06aMxZJJ28mfd3POQ", label: "Sam — casual" },
];

const CATALOG_VOICE_ID_SET = new Set(ELEVENLABS_VOICE_CATALOG.map((v) => v.id));
const CUSTOM_VOICE_SELECT = "__custom__";

function CardShell({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`p-6 rounded-2xl backdrop-blur-sm ${cardBorderClassName}`}
      style={{
        background: "rgba(26, 26, 36, 0.75)",
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-orange-600">
          <i className={`${icon} text-white text-xl`} />
        </div>

        <h2
          className="text-xl font-semibold text-white"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {title}
        </h2>
      </div>

      {children}
    </div>
  );
}

export default function AgentSetupPage() {
  const [agentName, setAgentName] = useState("");
  const [elevenVoiceId, setElevenVoiceId] = useState("");
  const [voiceStability, setVoiceStability] = useState(0.71);
  const [voiceSimilarity, setVoiceSimilarity] = useState(0.5);
  /** From GET /config only — informational while migrating to ElevenLabs /voice */
  const [legacyOpenaiVoice, setLegacyOpenaiVoice] = useState<string | null>(null);
  const [twilioNumber, setTwilioNumber] = useState("");

  const [existingGreeting, setExistingGreeting] = useState("");
  const [newCallerGreeting, setNewCallerGreeting] = useState("");

  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [isSavingVoice, setIsSavingVoice] = useState(false);
  const [isSavingNewGreeting, setIsSavingNewGreeting] = useState(false);
  const [isSavingExistingGreeting, setIsSavingExistingGreeting] = useState(false);

  const [saveMessage, setSaveMessage] = useState("");

  const getCustomerId = () =>
    localStorage.getItem("customerId") || sessionStorage.getItem("customerId");

  const flash = (msg: string) => {
    setSaveMessage(msg);
    window.setTimeout(() => setSaveMessage(""), 3000);
  };

  const loadCustomerConfig = async () => {
    try {
      const customerId = getCustomerId();
      if (!customerId) {
        console.warn("No customer ID found");
        setIsLoadingConfig(false);
        return;
      }

      const [res, voiceRes] = await Promise.all([
        fetch(`${API_BASE}/customers/${customerId}/config`, {
          method: "GET",
          credentials: "include",
        }),
        fetch(`${API_BASE}/customers/${customerId}/voice`, {
          method: "GET",
          credentials: "include",
        }),
      ]);

      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        console.error("Failed to load config:", res.status, errorText);
        setIsLoadingConfig(false);
        return;
      }

      const config = await res.json();

      setAgentName(config.agent?.agent_name || "");
      setLegacyOpenaiVoice(
        typeof config.agent?.openai_voice === "string" && config.agent.openai_voice.trim()
          ? config.agent.openai_voice.trim()
          : null
      );

      if (voiceRes.ok) {
        try {
          const voiceJson = await voiceRes.json();
          const s = voiceJson?.settings;
          if (s && typeof s === "object") {
            setElevenVoiceId(typeof s.voice_id === "string" ? s.voice_id.trim() : "");
            if (typeof s.stability === "number" && !Number.isNaN(s.stability)) {
              setVoiceStability(s.stability);
            }
            const sim =
              typeof s.similarity_boost === "number" && !Number.isNaN(s.similarity_boost)
                ? s.similarity_boost
                : typeof s.clarity === "number" && !Number.isNaN(s.clarity)
                  ? s.clarity
                  : undefined;
            if (sim !== undefined) setVoiceSimilarity(sim);
          }
        } catch (e) {
          console.error("Failed to parse voice settings:", e);
        }
      }

      // Greetings mapping (support multiple backend shapes)
      const existingGreetingValue =
        config.agent?.existing_user_greeting ??
        config.greetings?.existing_user_greeting ??
        config.greeting_template?.existing ??
        config.phone?.greeting_template?.existing ??
        "";

      const newCallerGreetingValue =
        config.agent?.new_caller_greeting ??
        config.greetings?.new_caller_greeting ??
        config.greeting_template?.new ??
        config.phone?.greeting_template?.new ??
        "";

      setTwilioNumber(config.phone?.twilio_phone_number || "");
      setExistingGreeting(existingGreetingValue);
      setNewCallerGreeting(newCallerGreetingValue);
    } catch (error) {
      console.error("Failed to load customer config:", error);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  useEffect(() => {
    loadCustomerConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAgentName = async () => {
    try {
      setIsSavingAgent(true);
      setSaveMessage("");

      const customerId = getCustomerId();
      if (!customerId) {
        setSaveMessage("Error: No customer ID found. Please log in again.");
        return;
      }

      const res = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent: { agent_name: agentName },
        }),
      });

      if (res.ok) {
        flash("✅ Agent name saved!");
        await loadCustomerConfig();
      } else {
        const errorText = await res.text().catch(() => "");
        console.error("Save failed:", res.status, errorText);
        setSaveMessage(`Failed to save agent name (Status: ${res.status})`);
      }
    } catch (error) {
      console.error("Failed to save agent name:", error);
      setSaveMessage(
        `Error saving agent name: ${
          error instanceof Error ? error.message : "Network error"
        }`
      );
    } finally {
      setIsSavingAgent(false);
    }
  };

  const saveVoiceSelection = async () => {
    try {
      setIsSavingVoice(true);
      setSaveMessage("");

      const customerId = getCustomerId();
      if (!customerId) {
        setSaveMessage("Error: No customer ID found. Please log in again.");
        return;
      }

      const res = await fetch(`${API_BASE}/customers/${customerId}/voice`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          voice_id: elevenVoiceId.trim(),
          stability: voiceStability,
          similarity_boost: voiceSimilarity,
        }),
      });

      if (res.ok) {
        flash("✅ Voice saved!");
        await loadCustomerConfig();
      } else {
        let detail = "";
        try {
          const errBody = await res.json();
          detail = (errBody && (errBody.error || errBody.detail)) || "";
        } catch {
          detail = await res.text().catch(() => "");
        }
        console.error("Save failed:", res.status, detail);
        setSaveMessage(
          detail ? `Failed to save voice (Status: ${res.status}): ${detail}` : `Failed to save voice (Status: ${res.status})`
        );
      }
    } catch (error) {
      console.error("Failed to save voice:", error);
      setSaveMessage(
        `Error saving voice: ${
          error instanceof Error ? error.message : "Network error"
        }`
      );
    } finally {
      setIsSavingVoice(false);
    }
  };

  const saveGreetings = async (which: "new" | "existing") => {
    try {
      if (which === "new") setIsSavingNewGreeting(true);
      else setIsSavingExistingGreeting(true);

      setSaveMessage("");

      const customerId = getCustomerId();
      if (!customerId) {
        setSaveMessage("Error: No customer ID found. Please log in again.");
        return;
      }

      // Save both fields to keep server state consistent, but the UX is “Save this box”.
      const payload = {
        agent: {
          existing_user_greeting: existingGreeting ?? "",
          new_caller_greeting: newCallerGreeting ?? "",
        },
      };

      const res = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        flash(which === "new" ? "✅ New greeting saved!" : "✅ Returning greeting saved!");
        await loadCustomerConfig();
      } else {
        const errorText = await res.text().catch(() => "");
        console.error("Save failed:", res.status, errorText);
        setSaveMessage(`Failed to save greeting (Status: ${res.status})`);
      }
    } catch (error) {
      console.error("Failed to save greetings:", error);
      setSaveMessage(
        `Error saving greeting: ${
          error instanceof Error ? error.message : "Network error"
        }`
      );
    } finally {
      if (which === "new") setIsSavingNewGreeting(false);
      else setIsSavingExistingGreeting(false);
    }
  };

  return (
    <CoreLayout>
      <PageContainer>
        <PageHeader
          title="Agent Setup"
          subtitle="Name, voice, phone number, and initial greetings."
        />

        {/* Save Message */}
        {saveMessage && (
          <div
            className="max-w-7xl mb-4 p-4 rounded-lg text-center"
            style={{
              background:
                saveMessage.includes("Error") || saveMessage.includes("Failed")
                  ? "rgba(239, 68, 68, 0.2)"
                  : "rgba(34, 197, 94, 0.2)",
              border: `1px solid ${
                saveMessage.includes("Error") || saveMessage.includes("Failed")
                  ? "rgba(239, 68, 68, 0.5)"
                  : "rgba(34, 197, 94, 0.5)"
              }`,
              color:
                saveMessage.includes("Error") || saveMessage.includes("Failed")
                  ? "#fca5a5"
                  : "#86efac",
            }}
          >
            {saveMessage}
          </div>
        )}

        {isLoadingConfig ? (
          <div className="text-center py-12 text-purple-300">Loading configuration...</div>
        ) : (
          <div className="max-w-7xl space-y-6">
            {/* TOP ROW: Agent Name / Voice / Phone */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Agent Name */}
              <CardShell icon="ri-robot-line" title="Agent Name">
                <input
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="Enter agent name"
                  className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                />

                <button
                  onClick={saveAgentName}
                  disabled={isSavingAgent}
                  className="mt-4 w-full py-3 rounded-lg font-semibold transition-all"
                  style={{
                    background: isSavingAgent
                      ? "rgba(138, 43, 226, 0.4)"
                      : "linear-gradient(90deg, #8a2be2, #ff6a00)",
                    color: "white",
                    cursor: isSavingAgent ? "not-allowed" : "pointer",
                  }}
                >
                  {isSavingAgent ? "Saving..." : "💾 Save Agent Name"}
                </button>
              </CardShell>

              {/* Voice (ElevenLabs — /api/customers/:id/voice) */}
              <CardShell icon="ri-mic-line" title="Voice (ElevenLabs)">
                <label className="block text-xs text-gray-400 mb-1">Voice</label>
                <select
                  value={
                    CATALOG_VOICE_ID_SET.has(elevenVoiceId) ? elevenVoiceId : CUSTOM_VOICE_SELECT
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === CUSTOM_VOICE_SELECT) {
                      if (CATALOG_VOICE_ID_SET.has(elevenVoiceId)) setElevenVoiceId("");
                      return;
                    }
                    setElevenVoiceId(v);
                  }}
                  className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                >
                  {ELEVENLABS_VOICE_CATALOG.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
                  <option value={CUSTOM_VOICE_SELECT}>Custom voice ID…</option>
                </select>
                {!CATALOG_VOICE_ID_SET.has(elevenVoiceId) && (
                  <div className="mt-3">
                    <label className="block text-xs text-gray-400 mb-1">Custom voice ID</label>
                    <input
                      type="text"
                      value={elevenVoiceId}
                      onChange={(e) => setElevenVoiceId(e.target.value)}
                      placeholder="Paste ElevenLabs voice_id"
                      className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Voice Consistency (0–1)</label>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.01}
                      value={voiceStability}
                      onChange={(e) => setVoiceStability(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Voice Naturalness (0–1)</label>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.01}
                      value={voiceSimilarity}
                      onChange={(e) => setVoiceSimilarity(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
                {legacyOpenaiVoice ? (
                  <p className="mt-3 text-xs text-gray-500">
                    Legacy OpenAI realtime voice (config): <span className="text-gray-400">{legacyOpenaiVoice}</span>
                  </p>
                ) : null}

                <button
                  onClick={saveVoiceSelection}
                  disabled={isSavingVoice}
                  className="mt-4 w-full py-3 rounded-lg font-semibold transition-all"
                  style={{
                    background: isSavingVoice
                      ? "rgba(138, 43, 226, 0.4)"
                      : "linear-gradient(90deg, #8a2be2, #ff6a00)",
                    color: "white",
                    cursor: isSavingVoice ? "not-allowed" : "pointer",
                  }}
                >
                  {isSavingVoice ? "Saving..." : "💾 Save Voice"}
                </button>
              </CardShell>

              {/* Assigned Phone Number */}
              <CardShell icon="ri-phone-line" title="Assigned Phone Number">
                {twilioNumber ? (
                  <>
                    <input
                      type="text"
                      value={twilioNumber}
                      disabled
                      className="w-full px-4 py-3 rounded-lg bg-black/30 border border-purple-500/20 text-white/80"
                    />
                    <p
                      className="mt-3 text-sm text-gray-400 leading-relaxed"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {PHONE_FORWARDING_HINT}
                    </p>
                    <div className="mt-3 text-xs text-gray-400">
                      (Read-only for now — assignment happens in Twilio / backend.)
                    </div>
                  </>
                ) : (
                  <p
                    className="text-sm text-blue-200/90 leading-relaxed"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {PHONE_PROVISIONING_PLACEHOLDER}
                  </p>
                )}
              </CardShell>
            </div>

            {/* SECOND ROW: Greetings (two cards) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CardShell icon="ri-chat-smile-2-line" title="New Caller Greeting">
                <textarea
                  value={newCallerGreeting}
                  onChange={(e) => setNewCallerGreeting(e.target.value)}
                  placeholder="Greeting for brand new callers…"
                  className="w-full min-h-[180px] px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                />

                <div className="mt-2 text-xs text-gray-400">
                  Available variables: {'{agent_name}'}, {'{time_greeting}'}
                </div>

                <button
                  onClick={() => saveGreetings("new")}
                  disabled={isSavingNewGreeting}
                  className="mt-4 w-full py-3 rounded-lg font-semibold transition-all"
                  style={{
                    background: isSavingNewGreeting
                      ? "rgba(138, 43, 226, 0.4)"
                      : "linear-gradient(90deg, #8a2be2, #ff6a00)",
                    color: "white",
                    cursor: isSavingNewGreeting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSavingNewGreeting ? "Saving..." : "💾 Save New Greeting"}
                </button>
              </CardShell>

              <CardShell icon="ri-chat-voice-line" title="Returning Caller Greeting">
                <textarea
                  value={existingGreeting}
                  onChange={(e) => setExistingGreeting(e.target.value)}
                  placeholder="Greeting for returning callers…"
                  className="w-full min-h-[180px] px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                />

                <div className="mt-2 text-xs text-gray-400">
                  Available variables: {'{agent_name}'}, {'{user_name}'}
                </div>

                <button
                  onClick={() => saveGreetings("existing")}
                  disabled={isSavingExistingGreeting}
                  className="mt-4 w-full py-3 rounded-lg font-semibold transition-all"
                  style={{
                    background: isSavingExistingGreeting
                      ? "rgba(138, 43, 226, 0.4)"
                      : "linear-gradient(90deg, #8a2be2, #ff6a00)",
                    color: "white",
                    cursor: isSavingExistingGreeting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSavingExistingGreeting ? "Saving..." : "💾 Save Returning Greeting"}
                </button>
              </CardShell>
            </div>
          </div>
        )}
      </PageContainer>
    </CoreLayout>
  );
}
